import axios from "axios";
import apiURL from "../apiURL";
import { checkSessionExpiration } from "../checkSessionExpiration";
import { handleAxiosError } from "../axiosErrorHandler";

const baseURL = apiURL();

export interface IExpenseNotificationRow {
    expenseNotificationId: string;
    expenseRequestId: number;
    notificationType: string;
    title: string;
    message: string;
    isRead: boolean;
    createdAtUtc: string;
    readAtUtc?: string | null;
    triggeredByDisplayName?: string | null;
}

export interface IExpenseNotificationPage {
    items: IExpenseNotificationRow[];
    totalCount: number;
    unreadCount: number;
}

interface IGetProps {
    token: string;
    unreadOnly?: boolean;
    page?: number;
    pageSize?: number;
}

/** Returns only the authenticated employee's own notifications - the server ignores any
 * employee identifier and always resolves ownership from the JWT. */
export const getExpenseNotifications = async (
    props: IGetProps,
): Promise<IExpenseNotificationPage | undefined> => {
    const { token, unreadOnly, page, pageSize } = props;
    if (!checkSessionExpiration()) return undefined;

    try {
        const url = new URL(`${baseURL}expense-notifications`);
        if (unreadOnly) url.searchParams.append("unreadOnly", "true");
        if (page) url.searchParams.append("page", page.toString());
        if (pageSize) url.searchParams.append("pageSize", pageSize.toString());

        const response = await axios({
            method: "get",
            responseType: "json",
            url: url.toString(),
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data as IExpenseNotificationPage;
    } catch (error) {
        handleAxiosError(error, "/login");
        return undefined;
    }
};

export const getExpenseNotificationUnreadCount = async (
    token: string,
): Promise<number> => {
    if (!checkSessionExpiration()) return 0;
    try {
        const response = await axios({
            method: "get",
            responseType: "json",
            url: `${baseURL}expense-notifications/unread-count`,
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data?.unreadCount ?? 0;
    } catch (error) {
        handleAxiosError(error, "/login");
        return 0;
    }
};

export const markExpenseNotificationRead = async (
    token: string,
    expenseNotificationId: string,
): Promise<void> => {
    await axios.post(
        `${baseURL}expense-notifications/${expenseNotificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
    );
};

export const markAllExpenseNotificationsRead = async (
    token: string,
): Promise<void> => {
    await axios.post(
        `${baseURL}expense-notifications/read-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
    );
};
